// The GIF picker's loader (#736): holds which cells are on screen and near it, which pictures arrived, and which
// downloads run, and asks the shared rule (shared/src/gifGrid.ts) what to start and what to cancel after each change.
// A cell may hold its picture's address only while `released` says so; taking the address away cancels the download.
import { GIF_LOAD_LIMIT, gifLoadStep } from "@bloom/shared";

export class GifLoader {
  private visible = new Set<number>();
  private near = new Set<number>();
  private loaded = new Set<number>();
  private flight = new Set<number>();
  private listeners = new Set<() => void>();
  private depth = 0;
  private released = new Set<number>();
  /** Changes whenever `released` does, for useSyncExternalStore. */
  version = 0;
  /** Every address given out, and every download cancelled, since the start (counts for tests and the console). */
  started = 0;
  cancelled = 0;

  constructor(private readonly limit = GIF_LOAD_LIMIT) {}

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };
  getVersion = (): number => this.version;

  /** Whether cell `i` may hold its picture's address now. */
  has(i: number): boolean {
    return this.released.has(i);
  }
  /** On screen: its download comes first. */
  isVisible(i: number): boolean {
    return this.visible.has(i);
  }

  /** Several changes, one step (an IntersectionObserver reports a few cells at once). */
  batch(fn: () => void): void {
    this.depth++;
    try {
      fn();
    } finally {
      this.depth--;
    }
    this.step();
  }
  setVisible(i: number, on: boolean): void {
    if (on ? this.visible.has(i) : !this.visible.has(i)) return;
    if (on) this.visible.add(i);
    else this.visible.delete(i);
    this.step();
  }
  setNear(i: number, on: boolean): void {
    if (on ? this.near.has(i) : !this.near.has(i)) return;
    if (on) this.near.add(i);
    else this.near.delete(i);
    this.step();
  }
  /** The picture arrived, or failed: the place is free, and the cell keeps what it has. */
  done(i: number): void {
    this.flight.delete(i);
    this.loaded.add(i);
    this.step();
  }

  private step(): void {
    if (this.depth) return;
    const { start, cancel } = gifLoadStep({
      visible: [...this.visible],
      near: [...this.near],
      loaded: [...this.loaded],
      inFlight: [...this.flight],
      limit: this.limit,
    });
    if (!start.length && !cancel.length) return;
    for (const i of cancel) this.flight.delete(i);
    for (const i of start) this.flight.add(i);
    this.started += start.length;
    this.cancelled += cancel.length;
    this.released = new Set([...this.loaded, ...this.flight]);
    this.version++;
    for (const fn of this.listeners) fn();
  }
}
